/**
 * Platform HQ — Customer support tools.
 *
 * Owner-only utilities to diagnose and fix stuck customer accounts:
 *   • Lookup any user across ALL companies by partial email/name match
 *   • Emergency password reset (returns a temporary password the operator can
 *     share with the customer over a secure channel)
 *
 * Lives at /platform-admin/support, linked from the dashboard header.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { ResetHealthPanel } from '../components/platform/ResetHealthPanel';
import {
  ArrowLeft, Search, Loader2, KeyRound, AlertOctagon, Copy, Check, LifeBuoy, LogOut,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export default function PlatformSupport() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { admin, token, logout } = usePlatformAuth();

  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  // Reset password dialog state
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => { if (!admin) navigate('/platform-admin/signin'); }, [admin, navigate]);
  if (!admin) return null;

  const headers = { Authorization: `Bearer ${token}` };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    await doSearch(q);
  };

  const doSearch = async (query) => {
    const trimmed = (query || '').trim();
    if (trimmed.length < 2) {
      toast({ title: 'Type at least 2 characters', variant: 'destructive' });
      return;
    }
    setQ(trimmed);
    setSearching(true);
    try {
      const res = await axios.get(`${API}/platform/users/lookup`, { headers, params: { q: trimmed } });
      setResults(res.data?.results || []);
      if ((res.data?.results || []).length === 0) {
        toast({ title: 'No users found', description: `Nothing matched "${trimmed}".` });
      }
    } catch (err) {
      toast({
        title: 'Lookup failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSearching(false);
    }
  };

  const confirmReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await axios.post(
        `${API}/platform/users/${resetTarget.id}/reset-password`,
        {},
        { headers },
      );
      setResetResult(res.data);
      toast({ title: 'Password reset', description: `New temp password generated for ${res.data.user_email}` });
    } catch (err) {
      toast({
        title: 'Reset failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setResetting(false);
    }
  };

  const copyTemp = () => {
    if (!resetResult?.temporary_password) return;
    navigator.clipboard?.writeText(resetResult.temporary_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="platform-support">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/platform-admin/dashboard')} className="text-slate-300 hover:text-white" data-testid="support-back-btn">
              <ArrowLeft className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Dashboard</span>
            </Button>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}>
              <LifeBuoy className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate">Customer Support Tools</h1>
              <p className="text-xs text-slate-400 truncate">Diagnose stuck accounts across every HinesTime company</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/platform-admin/signin'); }} className="text-slate-300 hover:text-white" data-testid="support-logout-btn">
            <LogOut className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Search */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-100 flex items-center gap-2 mb-1">
              <Search className="w-4 h-4 text-blue-400" />Find a customer user
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              Search by partial email or name across <strong>every</strong> company. Useful when a customer reports they can't sign in
              (typos, case mismatches, wrong company, suspended status, etc.).
            </p>
            <form onSubmit={runSearch} className="flex flex-col sm:flex-row gap-2">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. chines@hissg.com or Corey"
                className="flex-1 bg-slate-800 border-slate-700 text-white"
                data-testid="support-search-input"
                autoFocus
              />
              <Button
                type="submit" disabled={searching || q.trim().length < 2}
                className="text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}
                data-testid="support-search-btn"
              >
                {searching ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching…</>) : (<><Search className="w-4 h-4 mr-2" />Search</>)}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {results.length > 0 && (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-5">
              <h2 className="font-semibold text-slate-100 mb-3">Results ({results.length})</h2>
              <div className="overflow-x-auto" data-testid="support-results-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Company</th>
                      <th className="py-2 pr-3">Role</th>
                      <th className="py-2 pr-3">Super</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((u) => (
                      <tr key={u.id} className="border-b border-slate-800/60">
                        <td className="py-2 pr-3 font-mono text-xs text-slate-200 break-all">{u.email}</td>
                        <td className="py-2 pr-3 text-slate-300">{u.name || '—'}</td>
                        <td className="py-2 pr-3 text-slate-300">{u.company_name || '—'}</td>
                        <td className="py-2 pr-3 text-slate-400 text-xs capitalize">{u.role || '—'}</td>
                        <td className="py-2 pr-3">
                          {u.is_super_user
                            ? <Badge className="bg-emerald-900 text-emerald-200 border border-emerald-700 text-xs">Super</Badge>
                            : <span className="text-slate-500 text-xs">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          {u.company_subscription_status === 'active'   && <Badge className="bg-emerald-900 text-emerald-200 border border-emerald-700">Active</Badge>}
                          {u.company_subscription_status === 'trial'    && <Badge className="bg-blue-900 text-blue-200 border border-blue-700">Trial</Badge>}
                          {u.company_subscription_status === 'suspended'&& <Badge className="bg-amber-900 text-amber-200 border border-amber-700">Suspended</Badge>}
                          {u.company_subscription_status === 'locked_out' && <Badge className="bg-red-900 text-red-200 border border-red-700">Locked</Badge>}
                          {!u.company_subscription_status && <span className="text-slate-500">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => { setResetTarget(u); setResetResult(null); }}
                            className="border-red-800 text-red-300 hover:bg-red-950/40"
                            data-testid={`support-reset-${u.id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5 mr-1.5" />Reset password
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Showing up to 20 matches. If the user you're looking for isn't here, the account was never created — re-register the company
                or check for typos in the email at sign-up.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Reset health */}
        <ResetHealthPanel headers={headers} onFind={(email) => doSearch(email)} />

        {/* Help card */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-100 mb-2">Common diagnostics</h2>
            <ul className="text-sm text-slate-300 space-y-1.5 list-disc pl-5">
              <li>Email shows up here but customer can't sign in → password mismatch. Use Reset password to issue a temporary one.</li>
              <li>Email does NOT show up at all → typo at registration. Re-register the company with the correct email.</li>
              <li>Email shows up but company status is "Suspended" or "Locked" → unsuspend / restore via the Dashboard table.</li>
              <li>Customer says reset-password email never arrived → likely SES bounce (new domain, no MX). Issue a manual reset here.</li>
            </ul>
          </CardContent>
        </Card>
      </main>

      {/* Reset password confirmation dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => {
        if (!o) { setResetTarget(null); setResetResult(null); setCopied(false); }
      }}>
        <DialogContent className="bg-slate-900 border-red-900/60 text-slate-100 max-w-lg" data-testid="support-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <AlertOctagon className="w-5 h-5" />Force password reset
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {resetResult
                ? `A new temporary password has been issued for ${resetResult.user_email}. Share it over a secure channel.`
                : `Generate a strong temporary password for ${resetTarget?.email}. They will be forced to choose a new password on their next sign-in.`}
            </DialogDescription>
          </DialogHeader>

          {!resetResult ? (
            <div className="space-y-2 text-sm text-slate-300">
              <p>This action:</p>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li>Overwrites the user's current password immediately</li>
                <li>Forces them to choose a new password on first sign-in</li>
                <li>Is logged to your Platform HQ audit history</li>
              </ul>
              <p className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900/50 rounded p-2 mt-3">
                Use only when email-based reset is failing or the customer explicitly asked for help.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300 text-xs">Temporary password (copy now — won't be shown again)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code
                    className="text-base bg-slate-800 border border-slate-700 px-3 py-2 rounded break-all flex-1 font-mono text-emerald-300"
                    data-testid="support-temp-password"
                  >
                    {resetResult.temporary_password}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyTemp} className="text-slate-300 hover:text-white" data-testid="support-copy-temp">
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-400">{resetResult.note}</p>
            </div>
          )}

          <DialogFooter>
            {!resetResult ? (
              <>
                <Button variant="ghost" onClick={() => setResetTarget(null)} disabled={resetting} data-testid="support-reset-cancel">Cancel</Button>
                <Button
                  onClick={confirmReset} disabled={resetting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="support-reset-confirm"
                >
                  {resetting
                    ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting…</>)
                    : (<><KeyRound className="w-4 h-4 mr-2" />Reset password now</>)}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => { setResetTarget(null); setResetResult(null); setCopied(false); }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="support-reset-done"
              >
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
